import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Modal, Spinner } from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Delete from "../../assets/images/delete.svg";
import EditIc from "../../assets/images/ic-edit.svg";
import ApiConfig from "../../api/ApiConfig";
import {
  simplePostCall,
  simplePUTCall,
  simpleDeleteCall,
  getWithAuthCall,
} from "../../api/ApiServices";
import { notifySuccess, notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { StatusBadge } from "./ffUi";
import {
  ACCOUNT_TYPES,
  STATEMENTS,
  ACCOUNT_TYPE_LABELS,
  STATEMENT_LABELS,
  TCO_BUCKET_LABELS,
  isAccountCode,
  FfPagination,
} from "./ffHelpers";
import "../vehicleExpenses/vehicleAccident/VehicleExpenseHeader.scss";
import "./FleetFinance.scss";
import { useFfAccess } from "./ffAccess";

const PAGE_SIZE = 10;

// Server rule: asset / liability / equity -> balance_sheet, revenue / expense -> pnl
const statementForType = (type) =>
  ["revenue", "expense", "income"].includes(type) ? "pnl" : "balance_sheet";

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

// FF-03 / FF-09: fallbacks until gl-account/options answers; the real lists
// come from the API so they always match what it stores.
const TCO_BUCKETS = ["acquisition", "operating", "ownership", "depreciation", "residual"];

// U-04: readable label for a stored code (falls back to a capitalised code)
const capitalise = (s) => (s ? String(s).charAt(0).toUpperCase() + String(s).slice(1) : "-");
const typeLabel = (v) => ACCOUNT_TYPE_LABELS[v] || capitalise(v);
const statementLabel = (v) => STATEMENT_LABELS[v] || capitalise(v);
const bucketLabel = (v) => TCO_BUCKET_LABELS[v] || capitalise(v);

// FF-09: keep a stored value that is not in the option list as an extra option
// instead of silently falling back to the first option.
const withValue = (list, value) =>
  value && !list.includes(value) ? [...list, value] : list;

const emptyForm = {
  account_code: "",
  account_name: "",
  account_type: "expense",
  normal_balance: "debit",
  statement: "pnl",
  tco_bucket: "",
  is_active: true,
  is_system: false,
};

const ChartOfAccounts = () => {
  const { sidebar } = useContext(AppContext);
  const { canManage } = useFfAccess();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [filterType, setFilterType] = useState("");
  // QA #2 / #4 / #5: server-side paging, search, sort and inactive filter
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [sort, setSort] = useState("newest");
  const [showInactive, setShowInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  // option lists from gl-account/options (never hard-coded values)
  const [options, setOptions] = useState({
    account_type: ACCOUNT_TYPES,
    normal_balance: ["debit", "credit"],
    statement: STATEMENTS,
    tco_bucket: TCO_BUCKETS,
  });

  const [showForm, setShowForm] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    getWithAuthCall(ApiConfig.FLEET_FINANCE_GL_ACCOUNT_OPTIONS)
      .then((data) => {
        const d = data?.data;
        if (!(data?.success || data?.result) || !d) return;
        // the API uses singular keys; accept the plural spelling too
        const list = (k) => d[k] || d[`${k}s`];
        setOptions((prev) => ({
          account_type: list("account_type") || prev.account_type,
          normal_balance: list("normal_balance") || prev.normal_balance,
          statement: list("statement") || prev.statement,
          tco_bucket: list("tco_bucket") || prev.tco_bucket,
        }));
      })
      .catch((err) => console.log("gl options err", err));
  }, []);

  // search applies 400 ms after the last keystroke
  useEffect(() => {
    const id = setTimeout(() => setAppliedSearch(search.trim()), 400);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [filterType, appliedSearch, sort, showInactive]);

  useEffect(() => {
    getAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterType, appliedSearch, sort, showInactive]);

  const getAccounts = () => {
    setLoading(true);
    const body = JSON.stringify({
      page,
      page_limit: PAGE_SIZE,
      sort,
      search: appliedSearch || undefined,
      account_type: filterType || undefined,
      include_inactive: showInactive || undefined, // FF-14
    });
    simplePostCall(ApiConfig.FLEET_FINANCE_GL_ACCOUNT_LIST, body)
      .then((data) => {
        if (data?.success || data?.result) {
          setAccounts(data?.data || []);
          setTotalCount(data?.total_count ?? (data?.data || []).length);
        } else {
          setAccounts([]);
          setTotalCount(0);
          notifyError(data?.message || data?.error || "Unable to load accounts");
        }
      })
      .catch((err) => {
        console.log("gl list err", err);
        setAccounts([]);
        setTotalCount(0);
      })
      .finally(() => setLoading(false));
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const pageRows = accounts;
  const rangeStart = pageRows.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const rangeEnd = (page - 1) * PAGE_SIZE + pageRows.length;

  const openAdd = () => {
    setIsEdit(false);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (acc) => {
    setIsEdit(true);
    setForm({
      gl_account_id: acc.gl_account_id,
      account_code: acc.account_code || "",
      account_name: acc.account_name || "",
      account_type: acc.account_type || "expense",
      normal_balance: acc.normal_balance || "debit",
      statement: acc.statement || "pnl",
      tco_bucket: acc.tco_bucket || "", // FF-03: keep "none", never default to operating
      // FF-14
      is_active: acc.is_active === undefined || acc.is_active === null ? true : !!acc.is_active,
      is_system: !!acc.is_system,
    });
    setShowForm(true);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value };
      // statement follows the type (the server rejects a mismatch)
      if (name === "account_type") next.statement = statementForType(value);
      return next;
    });
  };

  // system accounts: only Name and Active can change
  const locked = isEdit && form.is_system;

  const handleSubmit = (e) => {
    e.preventDefault();
    // FF-11: the code is only editable on add (disabled on edit, not sent in update)
    if (!isEdit && !isAccountCode(form.account_code)) {
      notifyError("Account code may contain only letters, numbers, dot and dash");
      return;
    }
    setSaving(true);
    if (isEdit) {
      const body = JSON.stringify(
        locked
          ? {
              gl_account_id: form.gl_account_id,
              account_name: form.account_name,
              is_active: !!form.is_active,
            }
          : {
              gl_account_id: form.gl_account_id,
              account_name: form.account_name,
              account_type: form.account_type,
              normal_balance: form.normal_balance,
              statement: form.statement,
              tco_bucket: form.tco_bucket || "", // "" clears the bucket
              is_active: !!form.is_active, // FF-14
            }
      );
      simplePUTCall(ApiConfig.FLEET_FINANCE_GL_ACCOUNT_UPDATE, body)
        .then(onSaved)
        .catch(onSaveErr)
        .finally(() => setSaving(false));
    } else {
      const body = JSON.stringify({
        account_code: form.account_code,
        account_name: form.account_name,
        account_type: form.account_type,
        normal_balance: form.normal_balance,
        statement: form.statement,
        tco_bucket: form.tco_bucket || null, // FF-03
      });
      simplePostCall(ApiConfig.FLEET_FINANCE_GL_ACCOUNT_ADD, body)
        .then(onSaved)
        .catch(onSaveErr)
        .finally(() => setSaving(false));
    }
  };

  const onSaved = (data) => {
    if (data?.success || data?.result) {
      notifySuccess(data?.message || (isEdit ? "Account updated" : "Account added"));
      setShowForm(false);
      // QA #4: a new account shows on top of page 1
      if (!isEdit && (sort !== "newest" || page !== 1)) {
        setSort("newest");
        setPage(1);
      } else {
        getAccounts();
      }
    } else {
      notifyError(data?.message || data?.error || "Unable to save account");
    }
  };

  const onSaveErr = (err) => {
    console.log("save acc err", err);
    notifyError("Something went wrong");
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleteModal(true);
  };

  const handleDelete = () => {
    simpleDeleteCall(
      ApiConfig.FLEET_FINANCE_GL_ACCOUNT_DELETE + deleteId,
      JSON.stringify({})
    )
      .then((data) => {
        if (data?.success || data?.result) {
          notifySuccess(data?.message || "Account deleted");
          getAccounts();
        } else {
          notifyError(data?.message || data?.error || "Unable to delete account");
        }
      })
      .catch((err) => {
        console.log("delete acc err", err);
        notifyError("Something went wrong");
      })
      .finally(() => {
        setDeleteModal(false);
        setDeleteId(null);
      });
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_COA">
        <div
          className="displayFlexInp- displayFlexInp-for-expense-header"
          style={{ justifyContent: "space-between", alignItems: "center" }}
        >
          <div className="d-flex align-items-center flex-wrap" style={{ gap: 12 }}>
            <div className="form_input_main" style={{ width: 240, marginBottom: 0 }}>
              <Form.Control
                className="innerCust"
                type="text"
                placeholder={t("Search code or name")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <div className="form_input_main" style={{ width: 190, marginBottom: 0 }}>
              <Form.Select
                className="innerCust"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="">{t("All Account Types")}</option>
                {options.account_type.map((tp) => (
                  <option key={tp} value={tp}>
                    {typeLabel(tp)}
                  </option>
                ))}
              </Form.Select>
            </div>
            <div className="form_input_main" style={{ width: 170, marginBottom: 0 }}>
              <Form.Select
                className="innerCust"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="newest">{t("Newest first")}</option>
                <option value="code">{t("Sort by code")}</option>
              </Form.Select>
            </div>
            <Form.Check
              type="checkbox"
              id="coa-show-inactive"
              label={t("Show inactive")}
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          </div>
          {canManage && (
            <button className="cx-btn-2" onClick={openAdd}>
              + {t("Add GL Account")}
            </button>
          )}
        </div>

        {loading ? (
          <Loader />
        ) : (
          <div className="main-master-wrapper">
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Code")}</th>
                      <th>{t("Account Name")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Normal Balance")}</th>
                      <th>{t("Statement")}</th>
                      <th>{t("TCO Bucket")}</th>
                      <th>{t("Status")}</th>
                      {canManage && <th>{t("Action")}</th>}
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {pageRows.length > 0 ? (
                      pageRows.map((acc) => (
                        <tr key={acc.gl_account_id}>
                          <td>{acc.account_code}</td>
                          <td>{acc.account_name}</td>
                          {/* U-04 */}
                          <td>{typeLabel(acc.account_type)}</td>
                          <td>{capitalise(acc.normal_balance)}</td>
                          <td>{statementLabel(acc.statement)}</td>
                          <td>{bucketLabel(acc.tco_bucket)}</td>
                          <td>
                            {/* FF-14 */}
                            {acc.is_active === undefined || acc.is_active === null ? (
                              "-"
                            ) : (
                              <StatusBadge
                                value={acc.is_active ? "Active" : "Inactive"}
                                variant={acc.is_active ? "success" : "muted"}
                              />
                            )}
                          </td>
                          {canManage && (
                            <td>
                              <div className="d-flex">
                                <img
                                  src={EditIc}
                                  alt="edit"
                                  style={{ cursor: "pointer", marginRight: 12 }}
                                  onClick={() => openEdit(acc)}
                                />
                                <img
                                  src={Delete}
                                  alt="delete"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => confirmDelete(acc.gl_account_id)}
                                />
                              </div>
                            </td>
                          )}
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={canManage ? 8 : 7}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {totalCount > 0 && (
              <FfPagination
                t={t}
                page={page}
                totalPages={totalPages}
                rangeStart={rangeStart}
                rangeEnd={rangeEnd}
                total={totalCount}
                onPage={setPage}
              />
            )}
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      <Modal
        show={showForm}
        onHide={() => setShowForm(false)}
        centered
        className="common-model"
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              {isEdit ? t("Edit GL Account") : t("Add GL Account")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {locked && (
              <p className="ff-hint" style={{ marginBottom: 12 }}>
                {t("System account: only the name and Active status can be changed.")}
              </p>
            )}
            <div className="row">
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Account Code")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="account_code"
                  value={form.account_code}
                  onChange={handleChange}
                  disabled={isEdit}
                  placeholder="6099"
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Account Name")} <span>&#42;</span>
                </Form.Label>
                <Form.Control
                  required
                  type="text"
                  name="account_name"
                  value={form.account_name}
                  onChange={handleChange}
                  placeholder="Misc Fleet Expense"
                />
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">{t("Type")}</Form.Label>
                <Form.Select
                  name="account_type"
                  value={form.account_type}
                  onChange={handleChange}
                  disabled={locked}
                >
                  {/* FF-03 / FF-09 / U-04 */}
                  {withValue(options.account_type, form.account_type).map((tp) => (
                    <option key={tp} value={tp}>
                      {typeLabel(tp)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Normal Balance")}
                </Form.Label>
                <Form.Select
                  name="normal_balance"
                  value={form.normal_balance}
                  onChange={handleChange}
                  disabled={locked}
                >
                  {withValue(options.normal_balance, form.normal_balance).map((nb) => (
                    <option key={nb} value={nb}>
                      {capitalise(nb)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("Statement")}
                </Form.Label>
                <Form.Select
                  name="statement"
                  value={form.statement}
                  onChange={handleChange}
                  disabled={locked}
                >
                  {/* FF-03 / FF-09 / U-04 */}
                  {withValue(options.statement, form.statement).map((s) => (
                    <option key={s} value={s}>
                      {statementLabel(s)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              <div className="col-md-6 form_input_main">
                <Form.Label className="common-labels">
                  {t("TCO Bucket")}
                </Form.Label>
                <Form.Select
                  name="tco_bucket"
                  value={form.tco_bucket}
                  onChange={handleChange}
                  disabled={locked}
                >
                  {/* FF-09 / U-04: blank option clears the bucket */}
                  <option value="">{t("— none —")}</option>
                  {withValue(options.tco_bucket, form.tco_bucket).map((b) => (
                    <option key={b} value={b}>
                      {bucketLabel(b)}
                    </option>
                  ))}
                </Form.Select>
              </div>
              {isEdit && (
                <div className="col-md-6 form_input_main">
                  {/* FF-14 */}
                  <Form.Check
                    type="checkbox"
                    id="gl-account-is-active"
                    name="is_active"
                    label={t("Active")}
                    checked={!!form.is_active}
                    onChange={handleChange}
                  />
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="pop-up-modal-footer">
            <div className="btn-wrapper">
              <button
                type="button"
                className="cx-btn-1"
                onClick={() => setShowForm(false)}
              >
                {t("Cancel")}
              </button>
              <button type="submit" className="cx-btn-2" disabled={saving}>
                {saving ? <Spinner animation="border" size="sm" /> : t("Save")}
              </button>
            </div>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete modal */}
      <Modal
        show={deleteModal}
        onHide={() => setDeleteModal(false)}
        centered
        className="common-model"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("Delete GL Account")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("Are you sure you want to delete this account")} ?
        </Modal.Body>
        <Modal.Footer className="pop-up-modal-footer">
          <div className="btn-wrapper">
            <button
              className="cx-btn-1"
              onClick={() => setDeleteModal(false)}
            >
              {t("Cancel")}
            </button>
            <button className="cx-btn-2" onClick={handleDelete}>
              {t("Yes")}
            </button>
          </div>
        </Modal.Footer>
      </Modal>
    </motion.div>
  );
};

export default ChartOfAccounts;
